import { IconCheck, IconSparkles } from "@tabler/icons-react"
import { motion, useReducedMotion } from "motion/react"
import { type FC, useEffect, useRef } from "react"
import { useNavigate, useSearchParams } from "react-router"

import Button from "~/components/Button"
import { useUser } from "~/contexts/UserContext"
import { showConfetti } from "~/utilities/celebration"
import routes from "~/utilities/routes"

import Layout, { Logo } from "../Layout"

const PlanStartedPage: FC = () => {
	const user = useUser()
	const navigate = useNavigate()
	const nextUrl = useSearchParams()[0].get("next") ?? routes.main.index()
	const celebrationRef = useRef<HTMLDivElement>(null)
	const prefersReducedMotion = useReducedMotion()

	useEffect(() => {
		if (prefersReducedMotion) return

		const timeout = window.setTimeout(async () => {
			const celebration = celebrationRef.current
			if (celebration === null) return

			await showConfetti(celebration)
		}, 450)

		return () => window.clearTimeout(timeout)
	}, [prefersReducedMotion])

	return (
		<Layout>
			<main className="flex w-full max-w-2xl flex-col items-center py-8 sm:py-12">
				<Logo className="mb-8" />
				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.7, rotate: -8 }}
					animate={{ opacity: 1, scale: 1, rotate: 0 }}
					transition={{ type: "spring", stiffness: 240, damping: 16 }}
					className="relative mb-5"
				>
					<div
						ref={celebrationRef}
						className="flex size-20 items-center justify-center rounded-full bg-[#d9f99d] text-[#365314] shadow-[0_12px_32px_rgba(63,98,18,0.18)] ring-8 ring-[#f4ffd9]"
					>
						<IconCheck className="size-10 stroke-[2.5]" />
					</div>
					<IconSparkles className="absolute -top-3 -right-7 size-7 text-[#f59e0b]" />
				</motion.div>

				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.12, duration: 0.4 }}
					className="flex max-w-xl flex-col items-center text-center"
				>
					<p className="mb-3 rounded-full bg-[#ecfccb] px-3 py-1 text-xs font-bold tracking-[0.16em] text-[#4d7c0f] uppercase">
						Plan started
					</p>
					<h1 className="text-3xl leading-tight font-bold tracking-[-0.025em] text-slate-950 sm:text-4xl">
						Great choice!
					</h1>
					<p className="mt-3 text-base leading-relaxed font-medium text-slate-500">
						Your plan is active and your academy just got a whole lot more capable. If you ever need help,
						we're waiting for you in the chat.
					</p>
				</motion.div>

				<motion.div
					initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.3, duration: 0.35 }}
					className="mt-8"
				>
					<Button
						variant="primary"
						className="rounded-xl px-8"
						onClick={async () => {
							await user.reload()
							navigate(nextUrl)
						}}
					>
						Continue
					</Button>
				</motion.div>
			</main>
		</Layout>
	)
}

export default PlanStartedPage
